<template>
  <div>
    <CommentFormNotification v-if="followersNotified" :followers="followers" />
    <div class="sf-commcard-reply-form tw-rounded" :class="hideSessionClass">
      <SfMarkdownEditor
        ref="sfMarkdownEditor"
        v-model="localTextState"
        :mention-usernames="mentionUsernames"
        :mention-accounts="mentionAccounts"
        :mention-recon-templates="mentionReconTemplates"
        :is-loading-accounts="isLoadingAccounts"
        :is-loading-recon-templates="isLoadingReconTemplates"
        :disable-mentions="mentionsDisabled"
        :pulse-usernames="pulseUsernames"
        :pulse-warning-active="pulseWarningActive"
        :square-bottom="pulseHintVisible"
        :placeholder="placeholder"
        data-testid="sf-commcard-reply-editor"
        @update:model-value="clearTextErrors"
      />
      <div class="tw-overflow-hidden tw-rounded-b-lg">
        <PulseMentionHint
          v-if="commentableType === 'Remark'"
          :text="localTextState ?? ''"
          :pulse-usernames="pulseUsernames"
          :period-private="periodPrivate"
          :period-name="periodName"
          @hint-visibility-changed="(value) => (pulseHintVisible = value)"
        />
      </div>
      <div v-if="hasErrors('text')" class="sf-input-errors">
        <div v-for="e in commsErrors.text" :key="e">{{ e }}</div>
      </div>
      <div
        v-if="allAttachments.length > 0"
        class="sf-commcard-reply-form-files"
        :class="transparentClass"
      >
        <div class="sf-level sf-level-small sf-block">
          <div
            v-for="attachment in allAttachments"
            :key="attachment.id"
            class="sf-label sf-below-1"
          >
            <span class="sf-level-item">{{ attachment.filename }}</span
            ><span
              class="sf-level-item fa fa-times sf-pointer"
              @click.prevent="handleDeleteAttachment(attachment)"
            ></span>
          </div>
        </div>
      </div>
      <div class="sf-commcard-reply-form-footer" :class="transparentClass">
        <div
          v-if="uploadInProgress"
          class="sf-progress sf-progress-tiny sf-bg-snow"
          style="width: 60px"
        >
          <div
            :style="{ width: uploadProgress + '%' }"
            class="sf-progress-complete sf-bg-primary"
          ></div>
        </div>
        <DirectUploadDropzone
          :multiple="true"
          :on-file-progress="updateUploadProgress"
          :on-file-uploaded="addLocalAttachment"
          ><div class="sf-a sf-font-xs sf-fwdb sf-pointer">
            {{ I18n.t('communication.comments.add_attachments') }}
          </div></DirectUploadDropzone
        >
        <SfButtonDs
          v-if="cancellable"
          size="sm"
          data-testid="comment-form-cancel"
          @click="handleCancel"
        >
          {{ I18n.t('common.form.cancel') }}
        </SfButtonDs>
        <SfButtonDs
          variant="primary"
          size="sm"
          data-testid="comment-form-submit"
          :disabled="uploadInProgress"
          @click="handleSubmit"
        >
          {{ I18n.t(!!id ? 'common.save' : 'common.reply') }}
        </SfButtonDs>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions, mapState } from 'vuex'
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia'

import attachmentsHelper from '../mixins/attachments-helper'
import truncate from '@app/utils/truncate'
import useCommsError from '@app/application/communication/sections/communication/composables/use-comms-form-errors'
import CommentFormNotification from './comment-form-notification.vue'
import DirectUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import SfMarkdownEditor from '@app/shared/vue/components/sf-markdown-editor/sf-markdown-editor'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { useMentionablesStore } from '@app/application/communication/store/mentionables'
import PulseMentionHint from '@app/shared/vue/components/new-remark/pulse-mention-hint.vue'

export default {
  components: {
    SfButtonDs,
    CommentFormNotification,
    DirectUploadDropzone,
    SfMarkdownEditor,
    PulseMentionHint,
  },

  mixins: [attachmentsHelper],

  props: {
    id: {
      type: Number,
      required: false,
      default: null,
    },

    text: {
      type: String,
      required: true,
    },

    followerIds: {
      type: Array,
      required: false,
      default: () => [],
    },

    commentableId: {
      type: Number,
      required: true,
    },

    commentableType: {
      type: String,
      required: true,
    },

    placeholder: {
      type: String,
      required: false,
      default: '',
    },

    focusForm: {
      type: Boolean,
      default: true,
    },

    attachmentIds: {
      type: Array,
      required: false,
      default: () => [],
    },

    variant: {
      type: String,
      required: false,
      default: 'default',
    },
    companyId: {
      type: Number,
      required: true,
    },
    ledgerId: {
      type: Number,
      required: true,
    },
    periodName: {
      type: String,
      required: false,
      default: '',
    },
    periodPrivate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['cancel', 'submit'],

  setup() {
    const { errors, hasErrors, handleErrors, clearTextErrors } = useCommsError()
    const hideSessionClass = useRedactSessionRecording()
    return {
      commsErrors: errors.errors,
      hasErrors,
      handleErrors,
      clearTextErrors,
      hideSessionClass,
    }
  },

  data() {
    return {
      localTextState: this.text,
      localAttachments: [],
      uploadProgress: 0,
      pulseHintVisible: false,
    }
  },

  computed: {
    ...mapState({
      mentionUsernames: (state) => state.ui.mentionUsernames,
      pulseUsernames: (state) => state.ui.pulseUsernames,
    }),
    ...mapPiniaState(useMentionablesStore, [
      'mentionAccounts',
      'mentionReconTemplates',
      'isLoadingAccounts',
      'isLoadingReconTemplates',
    ]),

    ...mapGetters(['userById', 'attachmentArray']),

    // Only remark replies carry a private period; gate editor and hint together.
    pulseWarningActive() {
      return this.periodPrivate && this.commentableType === 'Remark'
    },

    uploadInProgress() {
      return this.uploadProgress > 0 && this.uploadProgress < 100
    },

    attachments() {
      const contextualAttachments = this.attachmentArray.filter(
        (attachment) => attachment && this.attachmentIds.includes(attachment.id)
      )

      return contextualAttachments.map((attachment) => ({
        ...attachment,
        filename: truncate(attachment.filename),
      }))
    },

    allAttachments() {
      return this.attachments.concat(this.localAttachments)
    },

    followers() {
      return this.followerIds.map((id) => this.userById(id))
    },

    attachmentParams() {
      const params = []

      _.forEach(this.allAttachments, (attachment) => {
        if (attachment.isTemp) {
          params.push({
            documentDirectUploadPath: attachment.uploadPath,
          })
        } else {
          params.push({ id: attachment.id })
        }
      })

      return params
    },

    cancellable() {
      return (
        !!this.id ||
        this.commentableType === 'Remark' ||
        this.commentableType === 'AssistantCheck'
      )
    },

    commentParams() {
      return {
        text: this.localTextState,
        fileAttrs: this.attachmentParams,
      }
    },

    addAttachmentMessage() {
      return (
        '<span class="sf-pointer sf-a sf-font-xs sf-fwdb">' +
        I18n.t('communication.remark_form.add_attachments') +
        '<span>'
      )
    },

    transparentClass() {
      return this.variant === 'transparent' ? 'tw-bg-transparent' : ''
    },

    isCheckLink() {
      return this.commentableType === 'CheckLink'
    },

    followersNotified() {
      return !this.isCheckLink
    },

    mentionsDisabled() {
      return this.isCheckLink
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.focusForm) {
        this.setInputFocus()
      }
    })
    window.eventHub.$on('focusCommentForm', this.setInputFocusExternal)

    this.initialize(this.companyId, this.ledgerId)
  },
  beforeUnmount() {
    window.eventHub.$off('focusCommentForm', this.setInputFocusExternal)
  },
  methods: {
    ...mapActions([
      'postComment',
      'editComment',
      'removeEditCommentId',
      'removeNewCommentRemarkId',
      'removeNewCommentAssistantCheckId',
      'deleteAttachment',
    ]),
    ...mapPiniaActions(useMentionablesStore, ['initialize']),
    setInputFocus() {
      const editor = this.$refs.sfMarkdownEditor
      editor?.autoFocus()
    },

    setInputFocusExternal({ commentableType, commentableId }) {
      if (
        commentableType === this.commentableType &&
        commentableId === this.commentableId
      ) {
        this.setInputFocus()
      }
    },

    updateUploadProgress({ progress }) {
      this.uploadProgress = progress
    },

    clearForm() {
      this.localTextState = this.text
      this.localAttachments = []
      this.uploadProgress = 0
    },

    handleCancel() {
      if (this.id) {
        this.removeEditCommentId(this.id)
      } else if (this.commentableType === 'Remark') {
        this.removeNewCommentRemarkId(this.commentableId)
      } else if (this.commentableType === 'AssistantCheck') {
        this.removeNewCommentAssistantCheckId(this.commentableId)
      }
      this.$emit('cancel', {
        id: this.id,
        commentableType: this.commentableType,
        commentableId: this.commentableId,
      })
    },

    handleSubmit() {
      if (this.id) {
        this.editComment([
          this.commentableId,
          this.commentableType,
          this.id,
          this.commentParams,
        ])
          .then(this.clearForm)
          .catch(this.handleErrors)
      } else {
        this.postComment([
          this.commentableId,
          this.commentableType,
          this.commentParams,
        ])
          .then(this.clearForm)
          .catch(this.handleErrors)
      }

      this.$emit('submit', {
        id: this.id,
        commentableType: this.commentableType,
        commentableId: this.commentableId,
        commentParams: this.commentParams,
      })
    },

    handleDeleteAttachment(attachment) {
      if (attachment.isTemp) {
        const index = this.localAttachments.indexOf(attachment)
        this.localAttachments.splice(index, 1)
      } else {
        this.deleteAttachment([this.id, attachment.id])
      }
    },

    addLocalAttachment({ blob }) {
      this.localAttachments.push({
        id: blob.id,
        filename: blob.filename,
        type: blob.content_type,
        size: blob.byte_size,
        uploadPath: blob.signed_id,
        isTemp: true,
      })
    },
  },
}
</script>
